import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { compositionApi } from '@texas/api/endpoints/compositionApi';
import { UserType } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { convertToEnum } from '@texas/utils/helpers/enumHelpers';
import { userTypeShortName } from '@texas/utils/helpers/roleHelpers';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getHistoryText } from './shared';

interface Props extends SharedDisclosureProps {
  compositionId: number;
  name: string;
}

export function CompositionHistoryModal({
  compositionId,
  name,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation();

  return (
    <Modal
      isCentered={true}
      size="3xl"
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('history.historyFor', { name: name })}</ModalHeader>
        <ModalCloseButton />
        <Content compositionId={compositionId} />
      </ModalContent>
    </Modal>
  );
}

function Content({ compositionId }: { compositionId: number }) {
  const { t } = useTranslation();
  const {
    data: histories,
    refetch,
    loading,
    error,
  } = useApiResource(compositionApi.getHistory);

  useEffect(() => {
    refetch(compositionId);
  }, [refetch, compositionId]);

  return (
    <ModalBody mb={6}>
      {loading && <Spinner />}
      {error && <ErrorDetails error={error} />}
      {histories && histories.length == 0 && (
        <Text>{t('history.noHistory')}</Text>
      )}
      {!loading && histories && histories.length > 0 && (
        <TableContainer flexGrow={1}>
          <Table variant="configuration">
            <Thead>
              <Tr>
                <Th>{t('general.date')}</Th>
                <Th>{t('general.user')}</Th>
                <Th>{t('history.event')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {histories.map((history) => (
                <Tr key={history.id}>
                  <Td>{formatDate(Locale.En, history.date)}</Td>
                  <Td>
                    <Flex direction="column">
                      <Flex gap={1}>
                        <Text>{history.userName}</Text>
                        {history.userType && (
                          <Tag size="sm" lineHeight="inherit">
                            {userTypeShortName(
                              convertToEnum(UserType, history.userType),
                              t,
                            )}
                          </Tag>
                        )}
                      </Flex>
                      {history.userEmail && <Text>{history.userEmail}</Text>}
                    </Flex>
                  </Td>
                  <Td>
                    <Text whiteSpace="normal">
                      {getHistoryText(history, t)}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </ModalBody>
  );
}
